import { useState, useEffect } from 'react';
import { API_BASE_URL } from '../utils/apiConfig';
import { supabase } from '../utils/supabase';
import toast from 'react-hot-toast';

interface PauseDetailsProps {
    membershipId: string;
    onPauseDeleted: () => void;
}

interface Pause {
    id: string;
    startDate: string;
    endDate: string | null;
    reason: string | null;
    status: string;
    createdAt: string;
}

export default function PauseDetails({ membershipId, onPauseDeleted }: PauseDetailsProps) {
    const [pauses, setPauses] = useState<Pause[]>([]);
    const [loading, setLoading] = useState(true);
    const [deleting, setDeleting] = useState<string | null>(null);

    useEffect(() => {
        fetchPauses();
    }, [membershipId]);

    const fetchPauses = async () => {
        try {
            setLoading(true);
            const { data: { session } } = await supabase.auth.getSession();
            if (!session?.access_token) return;

            const response = await fetch(
                `${API_BASE_URL}/api/membership-management/pauses/membership/${membershipId}`,
                {
                    headers: {
                        'Authorization': `Bearer ${session.access_token}`,
                        'Content-Type': 'application/json'
                    }
                }
            );

            if (response.ok) {
                const data = await response.json();
                setPauses(data.data || []);
            }
        } catch (error) {
            console.error('Error fetching pauses:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleDeletePause = async (pauseId: string) => {
        if (!confirm('Are you sure you want to delete this pause? This action cannot be undone.')) return;

        try {
            setDeleting(pauseId);
            const { data: { session } } = await supabase.auth.getSession();
            if (!session?.access_token) {
                toast.error('Not authenticated');
                return;
            }

            const response = await fetch(
                `${API_BASE_URL}/api/membership-management/pauses/${pauseId}`,
                {
                    method: 'DELETE',
                    headers: {
                        'Authorization': `Bearer ${session.access_token}`,
                        'Content-Type': 'application/json'
                    }
                }
            );

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.error || 'Failed to delete pause');
            }

            toast.success('Pause deleted successfully');
            await fetchPauses();
            onPauseDeleted();
        } catch (error: any) {
            console.error('Error deleting pause:', error);
            toast.error(error.message || 'Failed to delete pause');
        } finally {
            setDeleting(null);
        }
    };

    if (loading) {
        return (
            <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                <div className="animate-pulse flex space-x-2 items-center">
                    <div className="h-4 w-4 bg-yellow-300 rounded-full"></div>
                    <div className="h-4 bg-yellow-300 rounded w-32"></div>
                </div>
            </div>
        );
    }

    if (pauses.length === 0) {
        return null;
    }

    const now = new Date();

    return (
        <div className="mt-4 pt-4 border-t border-gray-300">
            <h4 className="text-sm font-semibold text-gray-700 mb-3">Pause History</h4>
            <div className="space-y-2">
                {pauses.map((pause) => {
                    const isPast = pause.endDate && new Date(pause.endDate) < now;

                    return (
                        <div
                            key={pause.id}
                            className="bg-yellow-50 border border-yellow-200 rounded-lg p-3"
                        >
                            <div className="flex justify-between items-start">
                                <div className="flex-1">
                                    <div className="flex items-center gap-2 mb-1">
                                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${pause.status === 'active' ? 'bg-yellow-100 text-yellow-800' :
                                                pause.status === 'completed' ? 'bg-green-100 text-green-800' :
                                                    'bg-gray-100 text-gray-800'
                                            }`}>
                                            {pause.status.charAt(0).toUpperCase() + pause.status.slice(1)}
                                        </span>
                                        {isPast && (
                                            <span className="text-xs text-gray-500">(Past)</span>
                                        )}
                                    </div>
                                    <div className="text-xs text-gray-700">
                                        <div>
                                            <span className="font-medium">Start:</span>{' '}
                                            {new Date(pause.startDate).toLocaleDateString()}
                                        </div>
                                        {pause.endDate && (
                                            <div>
                                                <span className="font-medium">End:</span>{' '}
                                                {new Date(pause.endDate).toLocaleDateString()}
                                            </div>
                                        )}
                                        {pause.reason && (
                                            <div className="mt-1">
                                                <span className="font-medium">Reason:</span>{' '}
                                                <span className="text-gray-600">{pause.reason}</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                                {!isPast && (
                                    <button
                                        onClick={() => handleDeletePause(pause.id)}
                                        disabled={deleting === pause.id}
                                        className="ml-2 p-1.5 text-red-600 hover:bg-red-50 rounded transition-colors disabled:opacity-50"
                                        title="Delete pause"
                                    >
                                        {deleting === pause.id ? (
                                            <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                            </svg>
                                        ) : (
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                            </svg>
                                        )}
                                    </button>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
